import { Chip, styled } from 'opensolar-ui'
import { useTranslate } from 'ra-core'
import { FC, useCallback, useMemo, useState } from 'react'
import { generateEmbedUrl, sanitizeHtml } from '../utils'
import { sectionBaseStyles } from './common/styles'
import { VideoDialog } from './Videos'

interface PropTypes {
  body: string
  tags: string[]
  videoUrl?: string
  media?: string
  clickLogger?: (url: string) => void
}

const InfoWrapper = styled('div', { name: 'InfoWrapper' })(({ theme }) => ({
  ...sectionBaseStyles,
  display: 'flex',
  padding: '25px 20px',
  gap: 50,
  justifyContent: 'space-between',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}))

const SectionWrapper = styled('div', { name: 'SectionWrapper' })({
  lineHeight: '24px',
})

const MediaWrapper = styled('div', { name: 'MediaWrapper' })({
  flexGrow: 1,
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',

  '& img': {
    maxWidth: '50%',
    minWidth: 200,
  },
})

const SectionHeader = styled('h1', { name: 'SectionHeader' })({
  fontSize: 24,
  marginTop: 0,
  marginBottom: 20,
})

const TagHeader = styled('h4', { name: 'TagHeader' })({
  marginBottom: 10,
})

const TagChip = styled(Chip, { name: 'TagChip' })({
  marginRight: 5,
  borderRadius: 20,
})

const VideoWrapper = styled('div', { name: 'VideoWrapper' })({
  cursor: 'pointer',
})

const VideoPlayer = styled('iframe', { name: 'VideoPlayer' })(({ theme }) => ({
  width: 520,
  height: 290,
  pointerEvents: 'none',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    height: `calc(100vw * 9 / 16)`,
  },
}))

const Description: FC<PropTypes> = ({ body, tags, videoUrl, media, clickLogger }) => {
  const translate = useTranslate()
  const [playVideo, setPlayVideo] = useState<string | null>(null)
  const videoLink = useMemo(() => generateEmbedUrl(videoUrl), [videoUrl])

  const handleVideoClick = useCallback(() => {
    if (videoLink) {
      setPlayVideo(videoLink)
      clickLogger?.(videoLink)
    }
  }, [videoLink, clickLogger])

  return (
    <InfoWrapper>
      <SectionWrapper>
        {body && (
          <>
            <SectionHeader>{translate('Our Story')}</SectionHeader>
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(body) }} />
          </>
        )}
        {!!tags?.length && (
          <>
            <TagHeader>{translate('Solutions offered')}</TagHeader>
            {tags.map((tag, i) => (
              <TagChip key={'tag-' + i} label={translate(tag)} variant="outlined" />
            ))}
          </>
        )}
      </SectionWrapper>

      {videoUrl ? (
        <SectionWrapper>
          {videoLink ? (
            <>
              <VideoWrapper onClick={handleVideoClick}>
                <VideoPlayer
                  src={videoLink}
                  frameBorder={0}
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              </VideoWrapper>
              {playVideo && (
                <VideoDialog
                  videoLink={playVideo}
                  onClose={() => {
                    setPlayVideo(null)
                  }}
                />
              )}
            </>
          ) : (
            translate('Error: Invalid Video Link')
          )}
        </SectionWrapper>
      ) : (
        <MediaWrapper>{media && <img src={media} />}</MediaWrapper>
      )}
    </InfoWrapper>
  )
}

export default Description
